import {
  CarOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
  ShoppingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {Button, DatePicker, Form, Input, Select, Switch, Tag} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useDispatch, useSelector} from 'react-redux';

import {RqAccountantLookupAll, RqCreateBill, RqGetAllFinalProducts} from '../../../../reactQuery/apis';
import {
  resetBill,
  setBillRows,
  setBillType,
  setCustomer,
  setCustomerType,
  setPaymentMethod,
  setPaymentMode,
  setTransportDetails,
  setTransportEnabled,
} from '../../../../redux/authAction';
import {selectCart} from '../../../../redux/selectors';

import {GenerateBillPdf} from './GenerateBillPdf';
import {ChallanDownloadButton} from './TransportChalanPdf/TransportChalanPdf';

const BILL_TYPES = ['GST', 'NON_GST'];
const CUSTOMER_TYPES = ['VENDOR', 'CUSTOMER'];
const PAYMENT_METHODS = ['CASH', 'UPI', 'CARD', 'CREDIT', 'MIXED', 'CHEQUE', 'BANK_TRANSFER', 'ADVANCE'];
const PAYMENT_MODE = ['OFFLINE', 'ONLINE'];
const BILL_CATEGORIES = ['NORMAL', 'KUMKUM'];
const KUMKUM_HSN = '33049940';

const emptyRow = () => ({
  id: window.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
  productId: null,
  productName: '',
  categoryName: '',
  hsnCode: '',
  unit: '',
  quantity: '',
  bags: '',
  bagsWeight: '',
  unitPrice: '',
  discount: 0,
  cgst: 0,
  sgst: 0,
  igst: 0,
  _total: 0,
});

const fmtCur = v =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`;

const toAmountWords = n => {
  if (!n) {
    return 'ZERO RUPEES ONLY';
  }

  const ones = [
    '',
    'ONE',
    'TWO',
    'THREE',
    'FOUR',
    'FIVE',
    'SIX',
    'SEVEN',
    'EIGHT',
    'NINE',
    'TEN',
    'ELEVEN',
    'TWELVE',
    'THIRTEEN',
    'FOURTEEN',
    'FIFTEEN',
    'SIXTEEN',
    'SEVENTEEN',
    'EIGHTEEN',
    'NINETEEN',
  ];

  const tens = [
    '',
    '',
    'TWENTY',
    'THIRTY',
    'FORTY',
    'FIFTY',
    'SIXTY',
    'SEVENTY',
    'EIGHTY',
    'NINETY',
  ];

  const toWords = num => {
    if (num < 20) {
      return ones[num];
    }

    if (num < 100) {
      return tens[Math.floor(num / 10)] +
        (num % 10 ? ' ' + ones[num % 10] : '');
    }

    if (num < 1000) {
      return ones[Math.floor(num / 100)] +
        ' HUNDRED' +
        (num % 100 ? ' ' + toWords(num % 100) : '');
    }

    if (num < 100000) {
      return toWords(Math.floor(num / 1000)) +
        ' THOUSAND' +
        (num % 1000 ? ' ' + toWords(num % 1000) : '');
    }

    return toWords(Math.floor(num / 100000)) +
      ' LAKH' +
      (num % 100000 ? ' ' + toWords(num % 100000) : '');
  };

  return `${toWords(Math.round(n))} RUPEES ONLY`;
};

const Section = ({icon, title, children}) => (
  <div className={'rounded-lg border border-primary-200 bg-white mb-4 overflow-hidden shadow-sm'}>
    <div className={'flex items-center gap-2 px-4 py-2.5 bg-primary-50 border-b border-primary-200'}>
      <span className={'text-primary-800 text-sm'}>{icon}</span>
      <span className={'font-semibold uppercase tracking-wide text-xs'}>{title}</span>
    </div>
    <div className={'p-4'}>{children}</div>
  </div>
);

Section.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const BillingScreen = () => {
  const dispatch = useDispatch();
  const token = useSelector(state => state.auth?.tokenData);
  const cashier = useSelector(state => state.auth?.user);
  const billing = useSelector(selectCart) || {};

  const {
    rows = [],
    billType,
    customerType,
    paymentMethod,
    paymentMode,
    transportEnabled,
    customer: savedCustomer,
    transport: savedTransport,
  } = billing;

  useEffect(() => {
    if (!rows.length) {
      dispatch(setBillRows([emptyRow()]));
    }
  }, [rows, dispatch]);

  const {data: finalProducts = [], isLoading: loadingProducts} =
    RqGetAllFinalProducts(token);

  const {data: customers = [], isLoading: loadingCustomers} =
    RqAccountantLookupAll(token);

  const [customerForm] = Form.useForm();
  const [transportForm] = Form.useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [billDate, setBillDate] = useState(dayjs());
  const [billCategory, setBillCategory] = useState('NORMAL');

  const [billSeq, setBillSeq] = useState(() => {
    return Number(localStorage.getItem('billSeq')) || 1;
  });

  useEffect(() => {
    localStorage.setItem('billSeq', billSeq);
  }, [billSeq]);

  const generateBillNumber = useCallback((type, seq) => {
    const date = billDate || dayjs();
    const year = date.year();
    const startYear = date.month() >= 3 ? year : year - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    const fyString = `${startYear}-${endYearShort}`;
    const dd = date.format('DD');
    const seqPadded = String(seq).padStart(4, '0');

    return type === 'GST'
      ? `GST-SI-B-${fyString}-${dd}-${seqPadded}`
      : `NGST-SI-B-${fyString}-${dd}-${seqPadded}`;
  }, [billDate]);

  const generateChallanNumber = useCallback(seq => {
    const date = billDate || dayjs();
    const mm = date.format('MM');
    const dd = date.format('DD');
    const seqPadded = String(seq).padStart(4, '0');

    return `CH-SI-${mm}-${dd}-${seqPadded}`;
  }, [billDate]);

  // Alias for backward compatibility
  const challanSeq = billSeq;

  const buildInvoiceData = useCallback((
    customer,
    transport,
    validRows,
    productOptions,
    totals,
    rowsData,
    currentPaymentMethod,
    currentBillType,
    amountInWordsValue,
  ) => {
    const currentSeq = billSeq;

    const invoiceNo = generateBillNumber(
      currentBillType,
      currentSeq,
    );

    const selectedCustomer = customers.find(
      c => c.id === customer.customerId,
    );

    return {
      invoiceNo,
      invoiceDate: billDate
        ? billDate.format('DD/MM/YYYY')
        : dayjs().format('DD/MM/YYYY'),
      ldNo: transport?.challanNumber || '',
      billType: currentBillType,
      billCategory,

      billing: {
        customerName: selectedCustomer
          ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
          : '',
        address: customer.customerAddress || '',
        district: customer.shippingDistrict || '',
        cellNo: customer.customerMobile || '',
        gstin: customer.gstIn || 'N/A',
        businessName: customer.businessName || '',
        ldnumber: customer.ldnumber || '',
        stateCode: '',
      },

      shipping: {
        customerName: selectedCustomer
          ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
          : '',
        address: customer.shippingAddress || customer.customerAddress || 'AS SAME',
        street: customer.shippingStreet || '',
        pinCode: customer.shippingPinCode || '',
        city: customer.shippingCity || '',
        district: customer.shippingDistrict || '',
        gstin: customer.gstIn || 'AS SAME',
        stateCode: 'AS SAME',
      },

      terms: {
        customerOrderNo: 'TELEPHONIC',
        termsOfPayment: '7 DAYS',
        deliveryNote: invoiceNo,
        dispatchedThrough: transport?.transportName || 'BY ROAD',
        destination: transport?.destination || '',
        motorVehicleNo: transport?.vehicleNumber || '',
        freight: transport?.freightPaid ? 'PAID' : 'TO PAY',
        modeOfPayment: paymentMode,
        ldNumber: customer.ldnumber || '',
      },

      rows: validRows.map(r => ({
        productLabel: billCategory === 'KUMKUM'
          ? (r.categoryName || r.productName)
          : (r.productName || productOptions.find(
            o => o.value === r.productId,
          )?.label || ''),
        hsnCode: billCategory === 'KUMKUM'
          ? KUMKUM_HSN
          : (r.hsnCode || ''),
        kgsBag: r.bagsWeight || 0,
        bags: r.bags || 0,
        qty: r.quantity || 0,
        rate: r.unitPrice || 0,
        total: r._total || 0,
        cgst: r.cgst || 0,
        sgst: r.sgst || 0,
        igst: r.igst || 0,
      })),

      totals: {
        bags: totals.bags,
        qty: totals.qty,
        total: totals.total,
        cgst: rowsData.reduce(
          (s, r) => s + (r.cgst || 0),
          0,
        ),
        sgst: rowsData.reduce(
          (s, r) => s + (r.sgst || 0),
          0,
        ),
        igst: rowsData.reduce(
          (s, r) => s + (r.igst || 0),
          0,
        ),
      },

      paidAmount: Number(customer.paidAmount) || 0,
      pendingAmount:
        totals.total - (Number(customer.paidAmount) || 0),
      amountInWords: amountInWordsValue,
    };
  }, [
    customers,
    billDate,
    billCategory,
    billSeq,
    generateBillNumber,
    paymentMode,
  ]);

  const resetAll = useCallback(() => {
    dispatch(resetBill());
    customerForm.resetFields();
    transportForm.resetFields();
    setPreviewOpen(false);
    setInvoiceData(null);
    setBillDate(dayjs());
    setBillCategory('NORMAL');
  }, [dispatch, customerForm, transportForm]);

  const createBillMutation = RqCreateBill({
    onSuccess: (_data, variables) => {
      toast.success('Bill created successfully!');

      const {billData: bd} = variables;
      const customer = customerForm.getFieldsValue();
      const transport = transportEnabled
        ? transportForm.getFieldsValue()
        : null;

      const validRows = rows.filter(
        r => r.productId && r.quantity && r.unitPrice,
      );

      const data = buildInvoiceData(
        {...customer, customerName: bd.customerName},
        transport,
        validRows,
        productOptions,
        totals,
        rows,
        bd.paymentMethod,
        bd.billType,
        amountInWords,
      );

      setBillSeq(prev => prev + 1);

      setInvoiceData(data);
      setPreviewOpen(true);
    },

    onError: err => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create bill';

      toast.error(errorMessage);
    },
  });

  const productOptions = useMemo(
    () => finalProducts.map(p => ({
      label: p.productName || p.name || `Product #${p.id}`,
      value: p.id,
      data: p,
    })),
    [finalProducts],
  );

  const CustomersOptions = useMemo(
    () => customers.map(c => {
      const name =
        `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
        `User #${c.id}`;

      const role = c.roles || '';

      return {
        label: role ? `${name} (${role})` : name,
        value: c.id,
        data: c,
      };
    }),
    [customers],
  );

  const updateRow = useCallback((id, field, value) => {
    const newRows = rows.map(r => {
      if (r.id !== id) {
        return r;
      }

      const updated = {...r, [field]: value};

      if (field === 'productId') {
        const prod = finalProducts.find(
          p => p.id === value,
        );

        const cgstVal = prod?.tax?.cgst ?? 0;
        const sgstVal = prod?.tax?.sgst ?? 0;
        const igstVal =
          prod?.tax?.igst ?? (cgstVal + sgstVal);

        updated.productName =
          prod?.productName || prod?.name || '';

        updated.categoryName =
          prod?.category || '';

        updated.hsnCode =
          prod?.hsnCode || '';

        updated.cgst = cgstVal;
        updated.sgst = sgstVal;
        updated.igst = igstVal;

        updated.unit =
          prod?.unit || prod?.unitOfMeasure || '';

        updated.unitPrice =
          prod?.sellingPrice ?? prod?.price ?? '';
      }

      if (field === 'cgst' || field === 'sgst') {
        const currentCgst =
          Number(field === 'cgst' ? value : updated.cgst) || 0;

        const currentSgst =
          Number(field === 'sgst' ? value : updated.sgst) || 0;

        updated.igst = +(
          currentCgst + currentSgst
        ).toFixed(2);
      }

      if (field === 'bagsWeight' || field === 'bags') {
        const bagsWeight =
          Number(
            field === 'bagsWeight'
              ? value
              : updated.bagsWeight,
          ) || 0;

        const bags =
          Number(
            field === 'bags'
              ? value
              : updated.bags,
          ) || 0;

        if (bagsWeight > 0 && bags > 0) {
          updated.quantity = +(
            bagsWeight * bags
          ).toFixed(2);
        }
      }

      const qty =
        Number(updated.quantity) || 0;

      const price =
        Number(updated.unitPrice) || 0;

      updated._total = +(
        price * qty
      ).toFixed(2);

      return updated;
    });

    dispatch(setBillRows(newRows));
  }, [rows, finalProducts, dispatch]);

  const addRow = () => {
    dispatch(setBillRows([...rows, emptyRow()]));
  };

  const removeRow = id => {
    dispatch(
      setBillRows(
        rows.filter(r => r.id !== id),
      ),
    );
  };

  const totals = useMemo(() => {
    const qty = rows.reduce(
      (s, r) => s + (Number(r.quantity) || 0),
      0,
    );

    const bags = rows.reduce(
      (s, r) => s + (Number(r.bags) || 0),
      0,
    );

    const taxableValue = rows.reduce(
      (s, r) => s + (r._total || 0),
      0,
    );

    const total = rows.reduce(
      (sum, row) =>
        sum +
        row._total +
        (row._total *
          (Number(row.igst) || 0)) /
          100,
      0,
    );

    const disc = rows.reduce(
      (s, r) =>
        s +
        (Number(r.discount) || 0) *
          (Number(r.quantity) || 0),
      0,
    );

    return {
      qty,
      bags,
      total,
      disc,
      taxableValue,
    };
  }, [rows]);

  const watchedCustomer =
    Form.useWatch([], customerForm);

  const watchedTransport =
    Form.useWatch([], transportForm);

  useEffect(() => {
    customerForm.setFieldsValue(savedCustomer);
    transportForm.setFieldsValue(savedTransport);
  }, []);

  useEffect(() => {
    if (watchedCustomer) {
      dispatch(setCustomer(watchedCustomer));
    }
  }, [watchedCustomer, dispatch]);

  useEffect(() => {
    if (watchedTransport) {
      dispatch(
        setTransportDetails(watchedTransport),
      );
    }
  }, [watchedTransport, dispatch]);

  useEffect(() => {
    if (!paymentMode) {
      dispatch(setPaymentMode('OFFLINE'));
    }
  }, [paymentMode, dispatch]);

  const challanData = useMemo(() => {
    const validRows = rows.filter(
      r => r.productId && r.quantity && r.unitPrice,
    );

    if (
      !transportEnabled ||
      !validRows.length
    ) {
      return null;
    }

    const customerFields =
      customerForm.getFieldsValue();

    const transportFields =
      transportForm.getFieldsValue();

    const selectedCustomer = customers.find(
      c => c.id === customerFields.customerId,
    );

    const customerName = selectedCustomer
      ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
      : '';

    return {
      invoiceDate: billDate
        ? billDate.format('DD/MM/YYYY')
        : dayjs().format('DD/MM/YYYY'),
      customer: {
        ...customerFields,
        customerName,
      },
      transport: transportFields,
      rows: validRows,
      totals,
    };
  }, [
    rows,
    totals,
    transportEnabled,
    watchedCustomer,
    watchedTransport,
    customers,
    billDate,
    customerForm,
    transportForm,
  ]);

  const paidAmount =
    Form.useWatch(
      'paidAmount',
      customerForm,
    );

  const remainingAmount =
    totals.total -
    (Number(paidAmount) || 0);

  const amountInWords = useMemo(
    () => toAmountWords(remainingAmount),
    [remainingAmount],
  );

  const handleSubmit = async () => {
    try {
      await customerForm.validateFields();

      if (transportEnabled) {
        await transportForm.validateFields();
      }
    } catch {
      toast.error(
        'Please fill all required fields',
      );
      return;
    }

    const validRows = rows.filter(
      r => r.productId && r.quantity && r.unitPrice,
    );

    if (!validRows.length) {
      toast.error(
        'Add at least one product with quantity and price',
      );
      return;
    }

    const customer =
      customerForm.getFieldsValue();

    const transport = transportEnabled
      ? transportForm.getFieldsValue()
      : null;

    const currentSeq = billSeq;

    const billNumber = customer.InvoiceNo || generateBillNumber(
      billType,
      currentSeq,
    );

    const challanNumber = transportEnabled
      ? (
        transport?.challanNumber ||
        generateChallanNumber(billSeq)
      )
      : '';

    const billData = {
      billNumber,
      billType,
      billCategory,
      customerId: customer.customerId,
      customerType,

      customerName: (() => {
        const sel = customers.find(
          c => c.id === customer.customerId,
        );

        return sel
          ? `${sel.firstName || ''} ${sel.lastName || ''}`.trim()
          : '';
      })(),

      customerMobile:
        customer.customerMobile,

      customerAddress:
        customer.customerAddress,

      shippingAddress: {
        address:
          customer.shippingAddress || '',
        street:
          customer.shippingStreet || '',
        pinCode:
          customer.shippingPinCode || '',
        city:
          customer.shippingCity || '',
        district:
          customer.shippingDistrict || '',
      },

      gstIn: customer.gstIn || '',
      businessName:
        customer.businessName || '',
      ldnumber:
        customer.ldnumber || '',

      items: validRows.map(r => ({
        productId: r.productId,
        productName: r.productName,
        categoryName: r.categoryName,
        hsnCode: r.hsnCode,
        quantity: Number(r.quantity),
        unitPrice: Number(r.unitPrice),
        unit: r.unit || '',
        bags: Number(r.bags) || 0,
        bagsWeight:
          Number(r.bagsWeight) || 0,
        discount:
          Number(r.discount) || 0,
        cgst: Number(r.cgst) || 0,
        sgst: Number(r.sgst) || 0,
        igst: Number(r.igst) || 0,
      })),

      discountAmount:
        +totals.disc.toFixed(2),

      taxAmount:
        +(
          totals.total -
          totals.taxableValue
        ).toFixed(2),

      finalAmount:
        +totals.total.toFixed(2),

      paidAmount:
        Number(customer.paidAmount) || 0,

      pendingAmount:
        +(
          totals.total -
          (Number(customer.paidAmount) || 0)
        ).toFixed(2),

      paymentMethod,
      paymentMode,

      cashierId:
        cashier?.id || '',

      cashierName:
        cashier?.name || '',

      transportDetails: transportEnabled
        ? {
          enabled: true,
          challanNumber,
          transportName:
            transport.transportName || '',
          vehicleNumber:
            transport.vehicleNumber || '',
          driverName:
            transport.driverName || '',
          driverMobile:
            transport.driverMobile || '',
          destination:
            transport.destination || '',
          freightCharge:
            Number(
              transport.freightCharge,
            ) || 0,
          freightPaid:
            transport.freightPaid ?? false,
        }
        : {
          enabled: false,
        },

      notes:
        customer.notes || '',

      billDate: billDate
        ? billDate.toISOString()
        : dayjs().toISOString(),
    };

    console.log('FRONTEND BILL NUMBER:', billNumber);
    console.log('FRONTEND BILL DATA:', billData);
    createBillMutation.mutate({
      token,
      billData,
    });
  };

  const handleCustomerSelect = useCallback(
    customerId => {
      const selected = customers.find(
        c => c.id === customerId,
      );

      if (!selected) {
        return;
      }

      customerForm.setFieldsValue({
        customerId: selected.id,
        customerName:
          `${selected.firstName || ''} ${selected.lastName || ''}`.trim(),
        customerMobile:
          selected.mobile || '',
        customerAddress:
          selected.address || '',
        gstIn:
          selected.gstIn || '',
        businessName:
          selected.businessName || '',
        ldnumber:
          selected.ldnumber || '',
        shippingAddress:
          selected.address || '',
        shippingStreet:
          selected.street || '',
        shippingCity:
          selected.city || '',
        shippingDistrict:
          selected.district || '',
        shippingPinCode:
          selected.pinCode || '',
      });
    },
    [customers, customerForm],
  );

  const handleModalClose = () => {
    resetAll();
  };

  const tableHeaders = [
    'Sr',
    billCategory === 'KUMKUM'
      ? 'Category'
      : 'Product',
    'HSN Code',
    'Bags Wt (kg)',
    'Bags',
    'Qty',
    'Unit',
    'Unit Price (₹)',
    'Taxable Amt',
    'CGST %',
    'SGST %',
    'IGST %',
    'Total',
    '',
  ];

  useEffect(() => {
    if (
      transportEnabled &&
      !savedTransport?.challanNumber
    ) {
      transportForm.setFieldsValue({
        challanNumber:
          generateChallanNumber(
            billSeq,
          ),
      });
    }
  }, [
    transportEnabled,
    billSeq,
    generateChallanNumber,
    savedTransport,
    transportForm,
  ]);
  useEffect(() => {
    customerForm.setFieldsValue({
      InvoiceNo: generateBillNumber(billType, billSeq),
    });
  }, [billType, billSeq, generateBillNumber, customerForm]);

  const BAG_WEIGHT_OPTIONS = [
    {label: '5.0 ', value: 5.0},
    {label: '10.0 ', value: 10.0},
    {label: '20.0 ', value: 20.0},
    {label: '25.0 ', value: 25.0},
    {label: '50.0 ', value: 50.0},
  ];

  return (
    <div className={'md:p-6 p-2'}>
      <div className={'flex flex-wrap items-center gap-3 mb-5 p-3 rounded-lg bg-primary-50 border border-primary-100'}>
        <div className={'flex items-center gap-2 flex-1 min-w-[180px]'}>
          <span className={'text-xs font-semibold text-primary-500 uppercase whitespace-nowrap'}>
            Bill Type
          </span>

          <Select
            value={billType}
            onChange={v =>
              dispatch(setBillType(v))
            }
            size={'middle'}
            options={BILL_TYPES.map(t => ({
              label: t,
              value: t,
            }))}
            className={'w-28'}
          />
        </div>

        <div className={'flex items-center gap-2 flex-1 min-w-[180px]'}>
          <span className={'text-xs font-semibold text-primary-500 uppercase whitespace-nowrap'}>
            Customer Type
          </span>

          <Select
            value={customerType}
            onChange={v =>
              dispatch(setCustomerType(v))
            }
            size={'middle'}
            options={CUSTOMER_TYPES.map(t => ({
              label: t,
              value: t,
            }))}
            className={'w-32'}
          />
        </div>

        <div className={'flex items-center gap-2 flex-1 min-w-[180px]'}>
          <span className={'text-xs font-semibold text-primary-500 uppercase whitespace-nowrap'}>
            Payment
          </span>

          <Select
            value={paymentMethod}
            onChange={v =>
              dispatch(setPaymentMethod(v))
            }
            size={'middle'}
            options={PAYMENT_METHODS.map(m => ({
              label: m.replace('_', ' '),
              value: m,
            }))}
            className={'w-36'}
          />
        </div>

        <div className={'flex items-center gap-2 flex-1 min-w-[180px]'}>
          <span className={'text-xs font-semibold text-primary-500 uppercase whitespace-nowrap'}>
            Bill Category
          </span>

          <Select
            value={billCategory}
            onChange={v =>
              setBillCategory(v)
            }
            size={'middle'}
            options={BILL_CATEGORIES.map(t => ({
              label: t,
              value: t,
            }))}
            className={'w-32'}
          />
        </div>

        <div className={'flex items-center gap-2 flex-1 min-w-[200px]'}>
          <span className={'text-xs font-semibold text-primary-500 uppercase whitespace-nowrap'}>
            Bill Date
          </span>

          <DatePicker
            value={billDate}
            onChange={d =>
              setBillDate(d || dayjs())
            }
            format={'DD/MM/YYYY'}
            allowClear={false}
            size={'middle'}
            className={'w-36'}
          />
        </div>
      </div>

      <Section
        icon={<UserOutlined />}
        title={'Customer / Vendor Details'}
      >
        <Form
          form={customerForm}
          layout={'vertical'}
          requiredMark={false}
          size={'small'}
        >
          <div className={'grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-0'}>
            <Form.Item
              label={'Invoice No.'}
              name={'InvoiceNo'}
            >
              <Input
                placeholder={generateBillNumber(billType, billSeq)}
                className={'font-mono uppercase'}
                maxLength={35}
                allowClear
              />
            </Form.Item>
            <Form.Item
              label={'Customer Name'}
              name={'customerId'}
              rules={[
                {
                  required: true,
                  message:
                    'Customer name is required',
                },
              ]}
            >
              <Select
                size={'middle'}
                className={'w-full'}
                placeholder={'Search & select customer'}
                loading={loadingCustomers}
                options={CustomersOptions}
                onChange={handleCustomerSelect}
                showSearch
                allowClear
                onClear={() =>
                  customerForm.resetFields()
                }
                filterOption={(input, opt) =>
                  (opt?.label ?? '')
                    .toLowerCase()
                    .includes(
                      input.toLowerCase(),
                    )}
              />
            </Form.Item>

            <Form.Item
              label={'Mobile'}
              name={'customerMobile'}
              rules={[
                {
                  pattern:
                    /^[6-9]\d{9}$/,
                  message:
                    'Enter a valid 10-digit mobile number',
                },
              ]}
            >
              <Input
                placeholder={'9876543210'}
                maxLength={10}
                onKeyPress={e =>
                  !/[0-9]/.test(e.key) &&
                  e.preventDefault()
                }
              />
            </Form.Item>

            <Form.Item
              label={'Billing Address'}
              name={'customerAddress'}
            >
              <Input
                placeholder={'e.g. A/P KURUNDWAD, KOLHAPUR'}
              />
            </Form.Item>

            {(billType === 'GST' ||
              customerType === 'VENDOR') && (
              <>
                <Form.Item
                  label={'GSTIN'}
                  name={'gstIn'}
                  rules={[
                    {
                      pattern:
                        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/,
                      message:
                        'Enter a valid GSTIN',
                    },
                  ]}
                >
                  <Input
                    placeholder={'27ABCDE1234F1Z5'}
                    className={'font-mono uppercase'}
                    maxLength={15}
                  />
                </Form.Item>

                <Form.Item
                  label={'Business Name'}
                  name={'businessName'}
                  getValueFromEvent={e =>
                    e.target.value.replace(
                      /^\s+/,
                      '',
                    )
                  }
                >
                  <Input
                    placeholder={'e.g. ABC Traders'}
                  />
                </Form.Item>

                <Form.Item
                  label={'LD Number'}
                  name={'ldnumber'}
                  getValueFromEvent={e =>
                    e.target.value.replace(
                      /^\s+/,
                      '',
                    )
                  }
                >
                  <Input
                    placeholder={'e.g. ABC Traders'}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              label={'Notes'}
              name={'notes'}
              className={'md:col-span-3'}
            >
              <Input
                placeholder={'Any additional notes…'}
              />
            </Form.Item>
          </div>

          <div className={'mt-2 pt-3 border-t border-primary-100'}>
            <p className={'font-bold flex justify-start text-primary-800 uppercase tracking-wide mb-3'}>
              Shipping Address
            </p>

            <div className={'grid grid-cols-1 md:grid-cols-3 gap-x-5'}>
              <Form.Item
                label={'Address'}
                name={'shippingAddress'}
              >
                <Input
                  placeholder={'e.g. Plot No. 5, MIDC'}
                />
              </Form.Item>

              <Form.Item
                label={'Street'}
                name={'shippingStreet'}
              >
                <Input
                  placeholder={'e.g. Station Road'}
                />
              </Form.Item>

              <Form.Item
                label={'City'}
                name={'shippingCity'}
                rules={[
                  {
                    pattern:
                      /^[A-Za-z\s]+$/,
                    message:
                      'Only letters and spaces allowed',
                  },
                ]}
              >
                <Input
                  placeholder={'e.g. Kolhapur'}
                />
              </Form.Item>

              <Form.Item
                label={'District'}
                name={'shippingDistrict'}
                rules={[
                  {
                    pattern:
                      /^[A-Za-z\s]+$/,
                    message:
                      'Only letters and spaces allowed',
                  },
                ]}
              >
                <Input
                  placeholder={'e.g. Kolhapur'}
                />
              </Form.Item>

              <Form.Item
                label={'Pin Code'}
                name={'shippingPinCode'}
                rules={[
                  {
                    pattern:
                      /^[1-9][0-9]{5}$/,
                    message:
                      'Enter a valid 6-digit pin code',
                  },
                ]}
              >
                <Input
                  placeholder={'416012'}
                  maxLength={6}
                  className={'font-mono'}
                  onKeyPress={e =>
                    !/[0-9]/.test(e.key) &&
                    e.preventDefault()
                  }
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Section>

      <Section
        icon={<ShoppingOutlined />}
        title={'Products Supplied'}
      >
        {billCategory === 'KUMKUM' && (
          <div className={'mb-3'}>
            <Tag
              color={'purple'}
              className={
                'text-xs font-semibold uppercase'
              }
            >
              KUMKUM Bill — HSN {KUMKUM_HSN} will
              appear on invoice
            </Tag>
          </div>
        )}

        <div className={'overflow-x-auto'}>
          <table className={'w-full !rounded-md text-sm border-collapse'}>
            <thead>
              <tr className={'bg-primary-900 text-primary-50'}>
                {tableHeaders.map((h, i) => (
                  <th
                    key={i}
                    className={'border border-primary-300 px-2 py-1.5 text-center font-semibold whitespace-nowrap'}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row.id}
                  className={'hover:bg-primary-50'}
                >
                  <td className={'border border-primary-200 px-2 py-1 text-center text-xs'}>
                    {idx + 1}
                  </td>

                  <td className={'border border-primary-200 px-1 py-1 min-w-[170px]'}>
                    <Select
                      size={'middle'}
                      className={'w-full'}
                      placeholder={'Select product'}
                      loading={loadingProducts}
                      options={productOptions}
                      value={row.productId}
                      onChange={v =>
                        updateRow(
                          row.id,
                          'productId',
                          v,
                        )
                      }
                      showSearch
                      filterOption={(input, opt) =>
                        (opt?.label ?? '')
                          .toLowerCase()
                          .includes(
                            input.toLowerCase(),
                          )}
                    />

                    {row.productId &&
                      billCategory === 'KUMKUM' && (
                      <span className={'block mt-0.5 text-[10px] font-medium text-purple-600 italic truncate max-w-[160px]'}>
                        {row.categoryName || '—'}
                      </span>
                    )}
                  </td>

                  <td className={'border border-primary-200 px-2 py-1 text-center'}>
                    {row.hsnCode ? (
                      <div>
                        <span className={'font-mono text-xs font-semibold block'}>
                          {row.hsnCode}
                        </span>

                        {billCategory ===
                          'KUMKUM' && (
                          <span className={'text-[10px] text-purple-500 font-medium block'}>
                            Invoice: {KUMKUM_HSN}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className={'text-xs text-primary-300 italic'}>
                        —
                      </span>
                    )}
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Select
                      size={'middle'}
                      options={BAG_WEIGHT_OPTIONS}
                      value={row.bagsWeight || undefined}
                      onChange={v =>
                        updateRow(
                          row.id,
                          'bagsWeight',
                          v,
                        )
                      }
                      className={'w-24'}
                      placeholder={'Select bag capacity'}
                    />
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Input
                      size={'middle'}
                      type={'number'}
                      min={0}
                      step={'0.01'}
                      value={row.bags}
                      onChange={e =>
                        updateRow(
                          row.id,
                          'bags',
                          e.target.value,
                        )
                      }
                      className={'w-16'}
                      placeholder={'0'}
                    />
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Input
                      size={'middle'}
                      type={'number'}
                      min={0}
                      step={'0.01'}
                      value={row.quantity}
                      onChange={e =>
                        updateRow(
                          row.id,
                          'quantity',
                          e.target.value,
                        )
                      }
                      className={'w-16'}
                      placeholder={'0'}
                    />
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Select
                      size={'middle'}
                      value={row.unit}
                      onChange={value =>
                        updateRow(
                          row.id,
                          'unit',
                          value,
                        )
                      }
                      className={'w-20'}
                      placeholder={'Unit'}
                      options={[
                        {
                          value: 'KG',
                          label: 'KG',
                        },
                        {
                          value: 'LITER',
                          label: 'Ltr',
                        },
                      ]}
                    />
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Input
                      size={'middle'}
                      type={'number'}
                      min={0}
                      step={'0.01'}
                      value={row.unitPrice}
                      onChange={e =>
                        updateRow(
                          row.id,
                          'unitPrice',
                          e.target.value,
                        )
                      }
                      className={'w-24'}
                      placeholder={'0.00'}
                    />
                  </td>

                  <td className={'border border-primary-200 px-2 py-1 text-center font-semibold whitespace-nowrap'}>
                    {row._total > 0
                      ? fmtCur(row._total)
                      : '—'}
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Input
                      size={'small'}
                      type={'number'}
                      min={0}
                      max={100}
                      step={'0.01'}
                      value={row.cgst}
                      onChange={e =>
                        updateRow(
                          row.id,
                          'cgst',
                          Number(e.target.value),
                        )
                      }
                      className={'w-16 font-semibold'}
                      suffix={'%'}
                      disabled={!row.productId}
                    />
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Input
                      size={'small'}
                      type={'number'}
                      min={0}
                      max={100}
                      step={'0.01'}
                      value={row.sgst}
                      onChange={e =>
                        updateRow(
                          row.id,
                          'sgst',
                          Number(e.target.value),
                        )
                      }
                      className={'w-16 font-semibold'}
                      suffix={'%'}
                      disabled={!row.productId}
                    />
                  </td>

                  <td className={'border border-primary-200 px-1 py-1'}>
                    <Input
                      size={'small'}
                      type={'number'}
                      min={0}
                      max={100}
                      step={'0.01'}
                      value={row.igst}
                      onChange={e =>
                        updateRow(
                          row.id,
                          'igst',
                          Number(e.target.value),
                        )
                      }
                      className={'w-16 font-semibold'}
                      suffix={'%'}
                      disabled={!row.productId}
                    />
                  </td>

                  <td className={'border border-primary-200 px-2 py-1 text-center font-semibold whitespace-nowrap'}>
                    {row._total > 0
                      ? fmtCur(
                        row._total +
                          (row._total *
                            (Number(row.igst) || 0)) /
                            100,
                      )
                      : '—'}
                  </td>

                  <td className={'border border-primary-200 px-1 py-1 text-center'}>
                    {rows.length > 1 && (
                      <Button
                        size={'small'}
                        danger
                        onClick={() =>
                          removeRow(row.id)
                        }
                        className={'w-6 h-6 p-0 flex items-center justify-center'}
                      >
                        <DeleteOutlined className={'text-xs'} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr className={'bg-primary-50 font-bold'}>
                <td
                  className={'border border-primary-300 px-2 py-1.5 text-center text-xs'}
                  colSpan={3}
                >
                  TOTAL
                </td>

                <td className={'border border-primary-300'}></td>

                <td className={'border border-primary-300 px-2 py-1.5 text-center'}>
                  {totals.bags || '—'}
                </td>

                <td className={'border border-primary-300 px-2 py-1.5 text-center'}>
                  {totals.qty}
                </td>

                <td className={'border border-primary-300'}></td>

                <td className={'border border-primary-300'}></td>

                <td className={'border border-primary-300 px-2 py-1.5 text-center text-indigo-700'}>
                  {fmtCur(totals.taxableValue)}
                </td>

                <td
                  className={'border border-primary-300'}
                  colSpan={3}
                ></td>

                <td className={'border border-primary-300 px-2 py-1.5 text-center text-indigo-700'}>
                  {fmtCur(totals.total)}
                </td>

                <td className={'border border-primary-300'}></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <Button
          onClick={addRow}
          icon={<PlusOutlined />}
          className={'mt-3 btn-secondary'}
          size={'middle'}
        >
          Add Row
        </Button>

        {totals.total > 0 && (
          <div className={'mt-3 p-3 flex justify-between bg-indigo-50 rounded-lg border border-indigo-200'}>
            <div className={'flex'}>
              <Form form={customerForm}>
                <Form.Item
                  label={'Paid Amount (₹)'}
                  name={'paidAmount'}
                >
                  <Input
                    type={'number'}
                    min={0}
                    step={'0.01'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Payment Mode'}
                  name={'paymentMode'}
                  initialValue={paymentMode || 'OFFLINE'}
                  rules={[
                    {
                      required: true,
                      message:
                        'Please select payment mode',
                    },
                  ]}
                >
                  <Select
                    placeholder={'Select Payment Mode'}
                    options={PAYMENT_MODE.map(mode => ({
                      label: mode,
                      value: mode,
                    }))}
                    onChange={value =>
                      dispatch(
                        setPaymentMode(value),
                      )
                    }
                  />
                </Form.Item>
              </Form>
            </div>

            <div>
              <p className={'text-lg flex justify-end'}>
                Remaining Amount:
                <span className={'font-bold pl-2'}>
                  ₹{Math.round(remainingAmount)}
                </span>
              </p>

              <p>
                Amount In Words :
                <span className={'font-bold'}>
                  ₹{amountInWords}.
                </span>
              </p>
            </div>
          </div>
        )}
      </Section>

      {totals.total > 0 && (
        <div className={'mb-4 px-4 py-2.5 rounded-lg border border-indigo-200 bg-indigo-50'}>
          <span className={'text-xs font-bold text-slate-950 uppercase tracking-wide'}>
            Total Amount in Words:{' '}
          </span>
          <span className={'font-bold text-indigo-800 uppercase'}>
            {toAmountWords(totals.total)}
          </span>
        </div>
      )}

      <Section
        icon={<CarOutlined />}
        title={'Transport Details'}
      >
        <div className={'flex items-center gap-3 mb-4'}>
          <Switch
            size={'middle'}
            checked={transportEnabled}
            onChange={v =>
              dispatch(
                setTransportEnabled(v),
              )
            }
            className={
              transportEnabled
                ? '!bg-emerald-700'
                : ''
            }
          />

          <span className={'text-sm font-medium'}>
            {transportEnabled
              ? 'Transport Enabled'
              : 'Enable Transport'}
          </span>

          {!transportEnabled && (
            <span className={'text-xs text-primary-400 italic'}>
              Toggle to add transport/delivery details
            </span>
          )}
        </div>

        {transportEnabled && (
          <>
            <Form
              form={transportForm}
              layout={'vertical'}
              requiredMark={false}
              size={'middle'}
            >
              <div className={'grid grid-cols-1 md:grid-cols-3 gap-x-5'}>
                <Form.Item
                  label={'Challan Number'}
                  name={'challanNumber'}
                >
                  <Input
                    className={'font-mono uppercase'}
                    placeholder={'e.g. CH-SI-09-09-0001'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Transport Name'}
                  name={'transportName'}
                >
                  <Input
                    placeholder={'e.g. Shri Ram Logistics'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Vehicle Number'}
                  name={'vehicleNumber'}
                >
                  <Input
                    placeholder={'e.g. MH 11 A 1836'}
                    className={'uppercase'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Driver Name'}
                  name={'driverName'}
                >
                  <Input
                    placeholder={'e.g. Raju Patil'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Driver Mobile'}
                  name={'driverMobile'}
                >
                  <Input
                    placeholder={'9876543210'}
                    maxLength={10}
                  />
                </Form.Item>

                <Form.Item
                  label={'Destination'}
                  name={'destination'}
                >
                  <Input
                    placeholder={'e.g. KURUNDWAD'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Freight Charge (₹)'}
                  name={'freightCharge'}
                >
                  <Input
                    type={'number'}
                    min={0}
                    step={'0.01'}
                    placeholder={'0'}
                  />
                </Form.Item>

                <Form.Item
                  label={'Freight Paid'}
                  name={'freightPaid'}
                  valuePropName={'checked'}
                  initialValue={false}
                >
                  <Switch size={'middle'} />
                </Form.Item>
              </div>
            </Form>

            <div className={'mt-4 pt-4 border-t border-primary-100'}>
              <p className={'text-xs font-semibold text-primary-500 uppercase tracking-wide mb-2'}>
                Download Delivery Challan
              </p>

              <ChallanDownloadButton
                challanData={challanData}
                disabled={!challanData}
              />

              {!challanData && (
                <p className={'text-xs text-primary-400 mt-1 italic'}>
                  Add at least one product with quantity and price to enable
                  challan download.
                </p>
              )}
            </div>
          </>
        )}
      </Section>

      <div className={'flex justify-end gap-3 pt-2'}>
        <Button
          size={'large'}
          onClick={resetAll}
          className={'px-8'}
        >
          Reset
        </Button>

        <Button
          type={'primary'}
          size={'large'}
          icon={<FileTextOutlined />}
          onClick={handleSubmit}
          loading={createBillMutation.isPending}
          className={'btn-primary px-8'}
        >
          Create Bill
        </Button>
      </div>

      {invoiceData && (
        <GenerateBillPdf
          open={previewOpen}
          onClose={handleModalClose}
          invoiceData={invoiceData}
        />
      )}
    </div>
  );
};

BillingScreen.propTypes = {};

export default BillingScreen;
